import React, { useEffect, useState, useMemo } from "react";
import Styles from "./CompanyDetails.module.css";
import { getBankingListings } from "../../../Services/GNA/GetData";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import ViewInjectDrawal from "../../Modals/Banking/ViewInjectDrawal/ViewInjectDrawal";
import PreviewModal from "../../../commons/PreviewModal/PreviewModal";
import { getDocumentUrlByLedgerId } from "../../../Services/ApiCalling/banking";
import { downloadBankingSuperAdmin } from "../../../Services/GNA/AlterApi";
import { SuccessMessage } from "../../../commons/Toast/ErrorMessage";
import { useLocation } from "react-router-dom";
import ListingDetails from "../../Modals/Admin/ListingDetails/ListingDetails";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const DiscomDetails = ({ discom }) => {
  const location = useLocation();
  const companyId = location?.search?.split("=")[1];

  const [viewType, setViewType] = useState("details");
  const [bankingList, setBankingList] = useState(null);
  const [viewDetails, setViewDetails] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [previewModal, setPreviewModal] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState(null);
  const [listingView, setListingView] = useState({ open: false, data: null });

  // search + pagination
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    if (viewType === "listing") handleFetchBankingDetails();
  }, [viewType]);

  const handleFetchBankingDetails = async () => {
    const res = await getBankingListings({ companyId });
    if (res?.data?.code === 200) setBankingList(res.data?.data?.page);
  };

  const handleViewTermsDocument = async (termsDocumentId) => {
    if (!termsDocumentId) return;
    const response = await getDocumentUrlByLedgerId(termsDocumentId);
    const url = response?.data || response;
    if (url) {
      setPreviewFileUrl(url);
      setPreviewModal(true);
    }
  };

  const handleDownload = async (id, name, userId) => {
    const response = await downloadBankingSuperAdmin({ id, companyId, userId });
    const blob = response?.data;
    if (!blob) return;
    const url = window.URL.createObjectURL(
      new Blob([blob], { type: "application/pdf" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `listing_${name}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    SuccessMessage("Listing downloaded successfully!");
  };

  const canViewAction = (s) => s === "COMPLETED" || s === "PAST";

  // ── filtered + paginated ────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!bankingList) return [];
    const q = search.trim().toLowerCase();
    if (!q) return bankingList;
    return bankingList.filter((l) =>
      [l?.name, l?.status, l?.settlementRate, l?.bankingFactor]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [bankingList, search]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice(page * pageSize, (page + 1) * pageSize);

  const handleSearch = (val) => {
    setSearch(val);
    setPage(0);
  };

  return (
    <>
      {viewDetails && (
        <ViewInjectDrawal
          open={viewDetails}
          handleClose={() => setViewDetails(false)}
          data={selectedListing}
        />
      )}
      {previewModal && (
        <PreviewModal
          open={previewModal}
          onClose={() => {
            setPreviewModal(false);
            setPreviewFileUrl(null);
          }}
          fileUrl={previewFileUrl}
        />
      )}
      {listingView.open && (
        <ListingDetails
          open={listingView.open}
          handleClose={() => setListingView({ open: false, data: null })}
          data={listingView.data}
          type="banking"
        />
      )}

      {/* ── Content wrapper (matches Consumer/Generator padding) ── */}
      <div className={Styles.entityDetailWrapper}>
        {/* ── Sub-tabs ── */}
        <div className={Styles.subTabs}>
          {[
            { id: "details", label: "Details" },
            // { id: "listing", label: "Listing" }
          ].map((t) => (
            <button
              key={t.id}
              className={`${Styles.subTab} ${viewType === t.id ? Styles.subTabActive : ""}`}
              onClick={() => setViewType(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Details ── */}
        {viewType === "details" && (
          <div className={Styles.entityDetailSections}>
            <div className={Styles.secCard}>
              <div className={Styles.secTitle}>Discom Information</div>
              <div className={Styles.secFields}>
                <div className={Styles.field}>
                  <span className={Styles.fieldLabel}>Discom Name</span>
                  <span className={Styles.fieldValue}>
                    {discom?.discomName || "—"}
                  </span>
                </div>
                <div className={Styles.field}>
                  <span className={Styles.fieldLabel}>Acronym</span>
                  <span className={`${Styles.fieldValue} ${Styles.fieldMono}`}>
                    {discom?.acronym || "—"}
                  </span>
                </div>
                <div className={Styles.field}>
                  <span className={Styles.fieldLabel}>Created At</span>
                  <span className={Styles.fieldValue}>
                    {discom?.createdAt?.split("T")[0] || "—"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Listing ── */}
        {viewType === "listing" && (
          <div>
            {bankingList === null ? (
              <div className={Styles.listingEmpty}>Loading…</div>
            ) : (
              <>
                {/* Toolbar */}
                <div className={Styles.ltToolbar}>
                  <div className={Styles.ltSearchBox}>
                    <SearchIcon
                      style={{
                        fontSize: "15px",
                        color: "#a0aec0",
                        flexShrink: 0,
                      }}
                    />
                    <input
                      className={Styles.ltSearchInput}
                      type="text"
                      placeholder="Search listing ID, status…"
                      value={search}
                      onChange={(e) => handleSearch(e.target.value)}
                    />
                    {search && (
                      <button
                        className={Styles.ltClear}
                        onClick={() => handleSearch("")}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <div className={Styles.ltToolbarRight}>
                    <span className={Styles.ltResultCount}>
                      {filtered.length} result{filtered.length !== 1 ? "s" : ""}
                    </span>
                    <select
                      className={Styles.ltPageSizeSelect}
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(0);
                      }}
                    >
                      {PAGE_SIZE_OPTIONS.map((n) => (
                        <option key={n} value={n}>
                          {n} / page
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Header */}
                <div className={Styles.ltHead}>
                  <div style={{ flex: "2.2" }} className={Styles.ltHeadCell}>
                    Listing ID
                  </div>
                  <div
                    style={{ flex: "1.2" }}
                    className={`${Styles.ltHeadCell} ${Styles.ltCenter}`}
                  >
                    Injection/Drawal
                  </div>
                  <div
                    style={{ flex: "0.8" }}
                    className={`${Styles.ltHeadCell} ${Styles.ltCenter}`}
                  >
                    BF(%)
                  </div>
                  <div
                    style={{ flex: "1" }}
                    className={`${Styles.ltHeadCell} ${Styles.ltCenter}`}
                  >
                    SR(₹/kWh)
                  </div>
                  <div
                    style={{ flex: "0.7" }}
                    className={`${Styles.ltHeadCell} ${Styles.ltCenter}`}
                  >
                    T&C
                  </div>
                  <div
                    style={{ flex: "1.8" }}
                    className={`${Styles.ltHeadCell} ${Styles.ltCenter}`}
                  >
                    Expires on
                  </div>
                  <div
                    style={{ flex: "0.8" }}
                    className={`${Styles.ltHeadCell} ${Styles.ltCenter}`}
                  >
                    Download
                  </div>
                  <div
                    style={{ flex: "1.5" }}
                    className={`${Styles.ltHeadCell} ${Styles.ltCenter}`}
                  >
                    Status
                  </div>
                  <div
                    style={{ flex: "0.8" }}
                    className={`${Styles.ltHeadCell} ${Styles.ltCenter}`}
                  >
                    Action
                  </div>
                </div>

                {/* Rows */}
                {paginated.length === 0 ? (
                  <div className={Styles.ltNoResult}>
                    {search ? (
                      <>
                        No results for "<strong>{search}</strong>"
                      </>
                    ) : (
                      "No listings available"
                    )}
                  </div>
                ) : (
                  paginated.map((listing, idx) => (
                    <div key={idx} className={Styles.ltRow}>
                      <div style={{ flex: "2.2" }} className={Styles.ltCell}>
                        {listing?.name || "—"}
                      </div>
                      <div
                        style={{ flex: "1.2" }}
                        className={`${Styles.ltCell} ${Styles.ltLink} ${Styles.ltCenter}`}
                        onClick={() => {
                          setSelectedListing(listing);
                          setViewDetails(true);
                        }}
                      >
                        View
                      </div>
                      <div
                        style={{ flex: "0.8" }}
                        className={`${Styles.ltCell} ${Styles.ltCenter}`}
                      >
                        {listing?.bankingFactor ?? "—"}
                      </div>
                      <div
                        style={{ flex: "1" }}
                        className={`${Styles.ltCell} ${Styles.ltCenter}`}
                      >
                        {listing?.settlementRate || "—"}
                      </div>
                      <div
                        style={{ flex: "0.7" }}
                        className={`${Styles.ltCell} ${Styles.ltCenter}`}
                      >
                        <button
                          className={`${Styles.ltIconBtn} ${!listing?.termsDocumentId ? Styles.ltIconBtnDisabled : ""}`}
                          title="View T&C"
                          disabled={!listing?.termsDocumentId}
                          onClick={() =>
                            handleViewTermsDocument(listing?.termsDocumentId)
                          }
                        >
                          <RemoveRedEyeIcon style={{ fontSize: "15px" }} />
                        </button>
                      </div>
                      <div
                        style={{ flex: "1.8" }}
                        className={`${Styles.ltCell} ${Styles.ltCenter}`}
                      >
                        {listing?.expiryAt || "—"}
                      </div>
                      <div
                        style={{ flex: "0.8" }}
                        className={`${Styles.ltCell} ${Styles.ltCenter}`}
                      >
                        <button
                          className={Styles.ltIconBtn}
                          title="Download"
                          onClick={() =>
                            handleDownload(
                              listing?.id,
                              listing?.name,
                              listing?.userId,
                            )
                          }
                        >
                          <DownloadIcon style={{ fontSize: "15px" }} />
                        </button>
                      </div>
                      <div
                        style={{ flex: "1.5" }}
                        className={`${Styles.ltCell} ${Styles.ltCenter}`}
                      >
                        <span
                          className={`${Styles.statusPill} ${Styles[`status_${listing?.status?.toLowerCase()}`] || ""}`}
                        >
                          {listing?.status || "—"}
                        </span>
                      </div>
                      <div
                        style={{ flex: "0.8" }}
                        className={`${Styles.ltCell} ${Styles.ltCenter}`}
                      >
                        <button
                          className={`${Styles.ltIconBtn} ${!canViewAction(listing?.status) ? Styles.ltIconBtnDisabled : ""}`}
                          title="View details"
                          disabled={!canViewAction(listing?.status)}
                          onClick={() =>
                            canViewAction(listing?.status) &&
                            setListingView({ open: true, data: listing })
                          }
                        >
                          <RemoveRedEyeIcon style={{ fontSize: "15px" }} />
                        </button>
                      </div>
                    </div>
                  ))
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className={Styles.ltPagination}>
                    <span className={Styles.ltPageInfo}>
                      {page * pageSize + 1}–
                      {Math.min((page + 1) * pageSize, filtered.length)} of{" "}
                      {filtered.length}
                    </span>
                    <div className={Styles.ltPageBtns}>
                      <button
                        className={Styles.ltPageBtn}
                        disabled={page === 0}
                        onClick={() => setPage((p) => p - 1)}
                      >
                        ‹
                      </button>
                      {Array.from(
                        { length: Math.min(totalPages, 7) },
                        (_, i) => {
                          const start = Math.max(
                            0,
                            Math.min(page - 3, totalPages - 7),
                          );
                          const pageNum = start + i;
                          return (
                            <button
                              key={pageNum}
                              className={`${Styles.ltPageBtn} ${pageNum === page ? Styles.ltPageBtnActive : ""}`}
                              onClick={() => setPage(pageNum)}
                            >
                              {pageNum + 1}
                            </button>
                          );
                        },
                      )}
                      <button
                        className={Styles.ltPageBtn}
                        disabled={page >= totalPages - 1}
                        onClick={() => setPage((p) => p + 1)}
                      >
                        ›
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {/* end entityDetailWrapper */}
    </>
  );
};

export default DiscomDetails;
